import * as ApiRequests from '../../api/requests';
import { History } from 'history';
import { pushAppRoute } from '../../data/domain/route-manager';
import { AuthenticationActions } from '../actions/authentication-actions';
import { AuthHelper } from '../../types';
import { AppThunk } from '../actions';
import { oktaSignOut, getOktaRefreshToken } from '../../auth';
import { HttpRequest } from '../../api/http-request';
import { SecurityLoginOkResponse } from '../../api/types';

export const AuthenticationThunks = {
    login: (
        oktaToken: string,
        onAuthenticated: (args: { email: string; userId: string; expirationDate: Date; rememberMe: boolean }) => void,
    ): AppThunk => async (): Promise<void> => {
        const refreshToken = await getOktaRefreshToken();
        const response = await HttpRequest.post({
            resource: '/api/security/login',
            body: { refresh_token: refreshToken },
            version: 3,
            headers: { Authorization: `Bearer ${oktaToken}` },
        });

        if (response.status >= 200 && response.status < 300) {
            const data = (response.response as SecurityLoginOkResponse).data;
            onAuthenticated({
                email: data.email,
                userId: data.id,
                expirationDate: new Date(data.expiration_date),
                rememberMe: true,
            });
        }
    },

    logout: (
        userId: string | undefined,
        history: History,
        onLoggedOut: () => void
    ): AppThunk => async (): Promise<void> => {
        // Important to route to login screen first otherwise user will be
        // redirected back to current page after they log back in, which is jarring,
        // and unexpected.
        pushAppRoute(history, { type: 'Login' });
        onLoggedOut();

        // Ideally the API call works and the user is "officially" logged out. But definitely
        // don't need to wait for this before letting the user think they're logged out. And
        // if this doesn't work, the reason is because the session has most likely already
        // expired.
        if (userId) {
            await ApiRequests.logoff({ client_type: 'Web' }, { authHelper: undefined });
        }

        // End the Okta session alongside the revoke call.
        await oktaSignOut();
    },

    setPassword: (code: string, password: string, authHelper?: AuthHelper): AppThunk => async (
        dispatch
    ): Promise<void> => {
        dispatch(AuthenticationActions.setPasswordStarted());

        const payload = await ApiRequests.resetPassword({ resetcode: code, newpassword: password }, { authHelper });

        if (payload.ok) {
            dispatch(AuthenticationActions.setPasswordSucceeded());

            return;
        }

        dispatch(AuthenticationActions.setPasswordFailed());
    },

    forgotPassword: (email: string): AppThunk => async (dispatch): Promise<void> => {
        dispatch(AuthenticationActions.resetPasswordStarted());

        const payload = await ApiRequests.resetPasswordRequest({ email }, { authHelper: undefined });

        if (payload.ok) {
            dispatch(AuthenticationActions.resetPasswordSucceeded());

            return;
        }

        dispatch(AuthenticationActions.resetPasswordFailed('Could not reset your password at this time.'));
    },

    verifyResetCode: (code: string): AppThunk => async (dispatch): Promise<void> => {
        dispatch(AuthenticationActions.verifyResetCodeStarted(code));

        const response = await ApiRequests.validateResetPasswordRequest({ resetcode: code }, { authHelper: undefined });

        if (response.ok) {
            dispatch(AuthenticationActions.verifyResetCodeSucceeded(code));

            return;
        }

        dispatch(AuthenticationActions.verifyResetCodeFailed(code));
    },
};
